import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { EyeIcon, EyeOffIcon } from '@untitledui-icons/react/line'
import { z } from 'zod'
import { AuthLayout } from '@/components/auth'
import { Button } from '@/components/base/buttons/button'
import { api, getErrorMessage } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'

// ---------- Schemas ----------

const loginSchema = z.object({
  email: z.string().min(1, 'app.validation.email_required').email('app.validation.invalid_email'),
  password: z.string().min(1, 'app.validation.password_required'),
})

type LoginFormValues = z.infer<typeof loginSchema>

const twoFactorSchema = z.object({
  code: z
    .string()
    .min(6, 'app.validation.2fa_min_digits')
    .max(6, 'app.validation.2fa_max_digits')
    .regex(/^\d{6}$/, 'app.validation.2fa_numbers_only'),
})

type TwoFactorFormValues = z.infer<typeof twoFactorSchema>

// ---------- Types ----------

interface OAuthAuthorizeResponse {
  redirect_url?: string
  requires_2fa?: boolean
  session_token?: string
}

interface OAuthParams {
  client_id: string
  redirect_uri: string
  state: string
  code_challenge: string
  code_challenge_method: string
  scope: string
}

// ---------- Helpers ----------

function getOAuthParams(searchParams: URLSearchParams): OAuthParams | null {
  const client_id = searchParams.get('client_id')
  const redirect_uri = searchParams.get('redirect_uri')
  const state = searchParams.get('state')
  const code_challenge = searchParams.get('code_challenge')
  const code_challenge_method = searchParams.get('code_challenge_method')

  if (!client_id || !redirect_uri || !code_challenge) {
    return null
  }

  return {
    client_id,
    redirect_uri,
    state: state ?? '',
    code_challenge,
    code_challenge_method: code_challenge_method ?? 'S256',
    scope: searchParams.get('scope') ?? '',
  }
}

// ---------- Component ----------

type Step = 'consent' | 'login' | '2fa' | 'redirecting' | 'done' | 'denied'

export function OAuthAuthorizePage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [showPassword, setShowPassword] = useState(false)
  const [credentials, setCredentials] = useState<LoginFormValues | null>(null)

  // Auth state
  const { user, accessToken, checkAuth, logout, getDecodedToken } = useAuthStore()

  // Determine initial step: show consent if already authenticated, login otherwise
  const [step, setStep] = useState<Step>(() => {
    // Check if auth is still valid (token not expired)
    const isValid = checkAuth()
    return isValid && accessToken ? 'consent' : 'login'
  })

  // Get user display info from token (sub = email in our JWT)
  const decodedToken = getDecodedToken()
  const userEmail = user?.email || decodedToken?.sub || null

  const oauthParams = getOAuthParams(searchParams)

  // Login form
  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  // 2FA form
  const twoFactorForm = useForm<TwoFactorFormValues>({
    resolver: zodResolver(twoFactorSchema),
    defaultValues: { code: '' },
  })

  // OAuth authorize mutation (credentials mode)
  const authorizeMutation = useMutation({
    mutationFn: async (data: {
      email?: string
      password?: string
      two_factor_code?: string
      access_token?: string
    }) => {
      if (!oauthParams) throw new Error('Missing OAuth parameters')
      const response = await api.post<OAuthAuthorizeResponse>('/oauth/authorize', {
        ...data,
        ...oauthParams,
      })
      return response.data
    },
    onSuccess: (data) => {
      if (data.requires_2fa) {
        setStep('2fa')
        return
      }
      // Persist the session so the user stays logged in after the OAuth flow
      if (data.session_token) {
        useAuthStore.getState().setTokens(data.session_token)
      }
      if (data.redirect_url) {
        setStep('redirecting')
        window.location.href = data.redirect_url
        // After a short delay, show a "done" screen in case the browser tab stays open
        setTimeout(() => setStep('done'), 2000)
      }
    },
    onError: () => {
      // If token-based auth fails (e.g. expired session), fall back to login
      if (step === 'consent') {
        setStep('login')
      }
    },
  })

  // ---------- Handlers ----------

  const onLoginSubmit = (data: LoginFormValues) => {
    setCredentials(data)
    authorizeMutation.mutate({ email: data.email, password: data.password })
  }

  const onTwoFactorSubmit = (data: TwoFactorFormValues) => {
    if (!credentials) return
    authorizeMutation.mutate({
      email: credentials.email,
      password: credentials.password,
      two_factor_code: data.code,
    })
  }

  const onConsentAuthorize = () => {
    if (!accessToken) {
      setStep('login')
      return
    }
    authorizeMutation.mutate({ access_token: accessToken })
  }

  const onConsentDeny = () => {
    // Don't redirect to the unvalidated redirect_uri — show a "denied" screen instead.
    // The redirect_uri comes from query params and hasn't been verified by the backend,
    // so redirecting to it could enable open-redirect / phishing attacks.
    setStep('denied')
  }

  const onUseDifferentAccount = () => {
    logout()
    setStep('login')
    authorizeMutation.reset()
  }

  // ---------- Invalid params ----------

  if (!oauthParams) {
    return (
      <AuthLayout
        title={t('app.auth.oauth_invalid_params')}
        subtitle={t('app.auth.oauth_invalid_params_description')}
      >
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          <p>{t('app.auth.oauth_invalid_params_description')}</p>
        </div>
      </AuthLayout>
    )
  }

  // ---------- Redirecting / Done ----------

  if (step === 'redirecting' || step === 'done') {
    return (
      <AuthLayout
        title={step === 'done' ? t('app.auth.oauth_done_title') : t('app.auth.oauth_redirecting')}
        subtitle={step === 'done' ? t('app.auth.oauth_done_subtitle') : undefined}
      >
        <div className="space-y-5">
          {step === 'redirecting' && (
            <div className="flex justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
            </div>
          )}
          {step === 'done' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-900/20">
                <svg className="h-6 w-6 text-brand-600 dark:text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <a
                href="/"
                className="text-sm text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400 dark:hover:text-brand-300"
              >
                {t('app.auth.oauth_go_to_dashboard')}
              </a>
            </div>
          )}
        </div>
      </AuthLayout>
    )
  }

  // ---------- Denied ----------

  if (step === 'denied') {
    return (
      <AuthLayout
        title={t('app.auth.oauth_denied_title')}
        subtitle={t('app.auth.oauth_denied_subtitle')}
      >
        <div className="flex flex-col items-center gap-4 py-4">
          <a
            href="/"
            className="text-sm text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400 dark:hover:text-brand-300"
          >
            {t('app.auth.oauth_go_to_dashboard')}
          </a>
        </div>
      </AuthLayout>
    )
  }

  // ---------- Consent Step (already logged in) ----------

  if (step === 'consent') {
    return (
      <AuthLayout
        title={t('app.auth.oauth_consent_title')}
        subtitle={t('app.auth.oauth_consent_subtitle')}
      >
        <div className="space-y-5">
          {/* Logged-in user info */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t('app.auth.oauth_consent_logged_in_as')}
            </p>
            {user?.fullName && (
              <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                {user.fullName}
              </p>
            )}
            <p className={`text-sm ${user?.fullName ? 'text-gray-500 dark:text-gray-400' : 'mt-1 font-semibold text-gray-900 dark:text-white'}`}>
              {userEmail || '—'}
            </p>
          </div>

          {/* Error message */}
          {authorizeMutation.error && (
            <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700 dark:bg-error-900/20 dark:text-error-400">
              {getErrorMessage(authorizeMutation.error)}
            </div>
          )}

          {/* Authorize / Deny buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              size="lg"
              color="primary"
              isDisabled={authorizeMutation.isPending}
              isLoading={authorizeMutation.isPending}
              showTextWhileLoading
              className="flex-1"
              onClick={onConsentAuthorize}
            >
              {authorizeMutation.isPending
                ? t('app.auth.oauth_authorizing')
                : t('app.auth.oauth_authorize')}
            </Button>
            <Button
              type="button"
              size="lg"
              color="secondary"
              isDisabled={authorizeMutation.isPending}
              className="flex-1"
              onClick={onConsentDeny}
            >
              {t('app.auth.oauth_deny')}
            </Button>
          </div>

          {/* Use different account link */}
          <div className="text-center">
            <button
              type="button"
              onClick={onUseDifferentAccount}
              className="text-sm text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400 dark:hover:text-brand-300"
            >
              {t('app.auth.oauth_consent_use_different_account')}
            </button>
          </div>
        </div>
      </AuthLayout>
    )
  }

  // ---------- 2FA Step ----------

  if (step === '2fa') {
    return (
      <AuthLayout
        title={t('app.auth.oauth_2fa_title')}
        subtitle={t('app.auth.oauth_2fa_subtitle')}
      >
        <form onSubmit={twoFactorForm.handleSubmit(onTwoFactorSubmit)} className="space-y-5">
          {/* 2FA code input */}
          <div>
            <label
              htmlFor="code"
              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('app.auth.verify_2fa')}
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={6}
              className={`
                w-full rounded-lg border px-3.5 py-2.5 text-center text-lg tracking-[0.5em]
                placeholder:text-gray-400
                focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0
                disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500
                dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500
                ${
                  twoFactorForm.formState.errors.code
                    ? 'border-error-500 focus:ring-error-500'
                    : 'border-gray-300 dark:border-gray-600'
                }
              `}
              disabled={authorizeMutation.isPending}
              {...twoFactorForm.register('code')}
            />
            {twoFactorForm.formState.errors.code && (
              <p className="mt-1.5 text-sm text-error-500">
                {t(twoFactorForm.formState.errors.code.message ?? '')}
              </p>
            )}
          </div>

          {/* Error message */}
          {authorizeMutation.error && (
            <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700 dark:bg-error-900/20 dark:text-error-400">
              {getErrorMessage(authorizeMutation.error)}
            </div>
          )}

          {/* Submit button */}
          <Button
            type="submit"
            size="lg"
            color="primary"
            isDisabled={authorizeMutation.isPending}
            isLoading={authorizeMutation.isPending}
            showTextWhileLoading
            className="w-full"
          >
            {authorizeMutation.isPending
              ? t('app.auth.oauth_authorizing')
              : t('app.auth.verify')}
          </Button>
        </form>
      </AuthLayout>
    )
  }

  // ---------- Login Step ----------

  return (
    <AuthLayout
      title={t('app.auth.oauth_sign_in_title')}
      subtitle={t('app.auth.oauth_sign_in_subtitle')}
    >
      <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-5">
        {/* Email field */}
        <div>
          <label
            htmlFor="email"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t('app.auth.email')}
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder={t('app.auth.enter_your_email')}
            className={`
              w-full rounded-lg border px-3.5 py-2.5 text-sm
              placeholder:text-gray-400
              focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0
              disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500
              dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500
              ${
                loginForm.formState.errors.email
                  ? 'border-error-500 focus:ring-error-500'
                  : 'border-gray-300 dark:border-gray-600'
              }
            `}
            disabled={authorizeMutation.isPending}
            {...loginForm.register('email')}
          />
          {loginForm.formState.errors.email && (
            <p className="mt-1.5 text-sm text-error-500">
              {t(loginForm.formState.errors.email.message ?? '')}
            </p>
          )}
        </div>

        {/* Password field */}
        <div>
          <label
            htmlFor="password"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t('app.auth.password')}
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder={t('app.auth.enter_your_password')}
              className={`
                w-full rounded-lg border px-3.5 py-2.5 pr-10 text-sm
                placeholder:text-gray-400
                focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0
                disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500
                dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500
                ${
                  loginForm.formState.errors.password
                    ? 'border-error-500 focus:ring-error-500'
                    : 'border-gray-300 dark:border-gray-600'
                }
              `}
              disabled={authorizeMutation.isPending}
              {...loginForm.register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOffIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>
          {loginForm.formState.errors.password && (
            <p className="mt-1.5 text-sm text-error-500">
              {t(loginForm.formState.errors.password.message ?? '')}
            </p>
          )}
        </div>

        {/* Error message */}
        {authorizeMutation.error && (
          <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700 dark:bg-error-900/20 dark:text-error-400">
            {getErrorMessage(authorizeMutation.error)}
          </div>
        )}

        {/* Submit button */}
        <Button
          type="submit"
          size="lg"
          color="primary"
          isDisabled={authorizeMutation.isPending}
          isLoading={authorizeMutation.isPending}
          showTextWhileLoading
          className="w-full"
        >
          {authorizeMutation.isPending
            ? t('app.auth.oauth_authorizing')
            : t('app.auth.sign_in')}
        </Button>
      </form>
    </AuthLayout>
  )
}

export default OAuthAuthorizePage
